import {
  AuthenticationDetails, CognitoUser, CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { MFAMethod } from './types';
import {
  getAWSCognitoUser, getAWSCognitoUserPool,
} from './utils';

/**
 * @returns Currently authenticated user object
 */
export const getCurrentUser = () => getAWSCognitoUserPool().getCurrentUser();

/**
 * @returns ID/Username of `getCurrentUser()`
 */
export const getCurrentUserId = () => getAWSCognitoUserPool().getCurrentUser()?.getUsername();

/**
 * @returns Currently authenticated user's Cognito groups
 */
export const getCurrentAWSCognitoGroups = () => new Promise<string[]>((resolve, reject) => {
  getCurrentUser()?.getSession((err: Error, session: any) => {
    if (err) reject(err);
    else resolve(session?.idToken?.payload['cognito:groups']);
  });
});

/**
 * @returns Currently authenticated user's access token
 */
export const getAccessToken = () => new Promise((resolve, reject) => {
  getCurrentUser()?.getSession((err: Error, session: any) => {
    if (err) reject(err);
    else resolve(session?.accessToken?.jwtToken);
  });
});

/**
 * Creates an unverified AWS Cognito account
 * @param email
 * @param password
 * @param phoneNumber
 */
export const signUp = async (
  email: string | undefined,
  password: string | undefined,
  phoneNumber: string | undefined,
  firstName?: string,
  lastName?: string) => new Promise<void>((resolve, reject) => {
  if (!email || !password || !phoneNumber) {
    reject(Error('Missing required details.'));
    return;
  }

  const emailAttribute = new CognitoUserAttribute({
    Name: 'email',
    Value: email,
  });

  const phoneNumberAttribute = new CognitoUserAttribute({
    Name: 'phone_number',
    Value: phoneNumber,
  });

  const firstNameAttribute = new CognitoUserAttribute({
    Name: 'given_name',
    Value: firstName || '',
  });

  const lastNameAttribute = new CognitoUserAttribute({
    Name: 'family_name',
    Value: lastName || '',
  });

  const userAttributes = [
    emailAttribute,
    phoneNumberAttribute,
    firstNameAttribute,
    lastNameAttribute,
  ];

  getAWSCognitoUserPool().signUp(email, password, userAttributes, [], (err) => {
    if (err) reject(err);
    else resolve();
  });
});

/**
 * Verifies an existing AWS Cognito account based on the SMS code delivered to user
 * @param email
 * @param verificationCode
 */
export const confirmSignUp = async (email: string | undefined, verificationCode: string) =>
  new Promise<void>((resolve, reject) => {
    getAWSCognitoUser(email)?.confirmRegistration(verificationCode, true, (err) => {
      if (err) reject(err);
      else resolve();
    });
  });

/**
 * Re-sends the verification code to the user's email
 * @param email
 * @returns
 */
export const resendConfirmationCode = async (email: string | undefined) =>
  new Promise<void>((resolve, reject) => {
    getAWSCognitoUser(email)?.resendConfirmationCode((err) => {
      if (err) reject(err);
      else resolve();
    });
  });

/**
 * Authenticates an AWS Cognito account, then stores it in local storage
 * @param email
 * @param password
 */
export type SignInResult = {
  user: CognitoUser;
  mfaRequired?: boolean;
  newPasswordRequired?: boolean;
  mfaMethod?: MFAMethod;
  session?: CognitoUserSession;
};

export const signIn = async (
  email: string,
  password: string,
): Promise<SignInResult> =>
  new Promise((resolve, reject) => {
    if (!email || !password) {
      reject(new Error('Missing email or password.'));
      return;
    }

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const user = getAWSCognitoUser(email);
    user?.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        resolve({ user, session });
      },
      onFailure: (err) => {
        reject(err);
      },
      mfaRequired: (mfaMethod) => {
        resolve({
          user,
          mfaRequired: true,
          mfaMethod: mfaMethod as MFAMethod,
        });
      },
      newPasswordRequired: () => {
        resolve({
          user,
          newPasswordRequired: true,
        });
      },
      totpRequired: () => {
        resolve({
          user,
          mfaRequired: true,
          mfaMethod: MFAMethod.TOTP,
        });
      },
    });
  });

/**
 * Sets a new password on top of the temporary password
 */
export const setNewPassword = async (
  user: CognitoUser | undefined,
  newPassword: string) => new Promise<SignInResult>((resolve, reject) => {
  if (!user) reject(Error('No user is currently authenticated.'));

  user?.completeNewPasswordChallenge(newPassword, {}, {
    onSuccess: () => resolve({ user }),
    onFailure: reject,
    mfaRequired: (mfaMethod) => {
      resolve({
        user,
        mfaRequired: true,
        mfaMethod: mfaMethod as MFAMethod,
      });
    },
    totpRequired: () => {
      resolve({
        user,
        mfaRequired: true,
        mfaMethod: MFAMethod.TOTP,
      });
    },
  });
});

/**
 * Checks the MFA code for a given user
 * @returns
 */
export const verifyMFA = async (
  user: CognitoUser | undefined, mfaMethod: string, verificationCode: string) =>
  new Promise<SignInResult>((resolve, reject) => {
    if (!user) reject(Error('No user is currently authenticated.'));

    user?.sendMFACode(verificationCode, {
      onSuccess: () => resolve({ user }),
      onFailure: reject,
    }, mfaMethod);
  });

/**
 * Removes the AWS Cognito account from the local storage
 */
export const signOut = async () => {
  const username = await getCurrentUserId();

  if (username) {
    return getAWSCognitoUser(username)?.signOut();
  }
  throw new Error('No user is currently authenticated.');
};

/**
 * Invalidates all session tokens associated with an AWS Cognito account
 */
export const globalSignOut = async () => {
  const username = await getCurrentUserId();

  if (username) {
    return new Promise((resolve, reject) => {
      getAWSCognitoUser(username)?.globalSignOut({
        onSuccess: resolve,
        onFailure: reject,
      });
    });
  }

  throw new Error('No user is currently authenticated.');
};

/**
 * Updates the password of the currently authenticated user
 * @param oldPassword
 * @param newPassword
 */
export const changePassword = async (oldPassword: string, newPassword: string) => {
  const username = await getCurrentUserId();

  if (username) {
    return new Promise<void>((resolve, reject) => {
      getAWSCognitoUser(username)?.changePassword(oldPassword, newPassword, (err) => {
        if (err) reject(err);
        else resolve();
      });
    });
  }
  throw new Error('No user is currently authenticated.');
};

/**
 * Sends a password reset code to the user's email
 * @param email
 */
export const sendPasswordResetCode = (
  email: string | undefined) => new Promise((resolve, reject) => {
  getAWSCognitoUser(email)?.forgotPassword({
    onSuccess: resolve,
    onFailure: reject,
  });
});

/**
 * Verifies the password reset code to update the user's password
 * This function should only be called after `sendPasswordResetCode()`
 * @param email
 * @param verificationCode
 * @param newPassword
 */
export const confirmPasswordResetCode = (
  email: string | undefined,
  verificationCode: string,
  newPassword: string) => new Promise((resolve, reject) => {
  getAWSCognitoUser(email)?.confirmPassword(verificationCode, newPassword, {
    onSuccess: resolve,
    onFailure: reject,
  });
});

/**
 * Provides the current user's AWS Cognito attributes through a callback
 * @param callback
 * @returns `null`
 */
export const getCurrentUserAttributes = async () =>
  new Promise<CognitoUserAttribute[] | undefined>((resolve, reject) => {
    const user = getCurrentUser();
    if (user) {
    // `getSession` must be called on the same instance of user for this function to work
      user?.getSession(() =>
        user.getUserAttributes((err, attributes) => {
          if (err) reject(err);
          else resolve(attributes);
        }),
      );
    } else {
      reject(Error('No user is currently authenticated.'));
    }
  });

/**
 * Sends an email to the current user containing the verification code
 */
export const sendEmailConfirmationCode = async () => new Promise((resolve, reject) => {
  const user = getCurrentUser();
  // `getSession` must be called on the same instance of user for this function to work
  if (user) {
    user?.getSession(() =>
      user.getAttributeVerificationCode('email', {
        onSuccess: resolve,
        onFailure: reject,
      }),
    );
  }
  reject(Error('No user is currently authenticated.'));
});

/**
 * Verifies an email for the current user via the emailed verification code
 * @param verificationCode
 */
export const confirmEmailConfirmationCode = async (
  verificationCode: string) => new Promise((resolve, reject) => {
  const user = getCurrentUser();
  if (user) {
    // `getSession` must be called on the same instance of user for this function to work
    user?.getSession(() =>
      user.verifyAttribute('email', verificationCode, {
        onSuccess: resolve,
        onFailure: reject,
      }),
    );
  }
  reject(Error('No user is currently authenticated.'));
});

/**
 * Async function returning whether or not a user is currently authenticated
 * @returns Authentication state
 */
export const getIsUserAuthenticated = async () => {
  const user = getCurrentUser();
  if (!user) return false;

  const getCurrentSession = () => new Promise((resolve) => {
    user.getSession((err: any, session: any) => {
      if (err || !session) resolve(false);
      if (session) resolve(true);
    });
  });

  const isSessionValid = await getCurrentSession();
  if (isSessionValid) return true;
  return false;
};

/**
 * Initiates the TOTP setup process and returns the secret code
 */
export const associateSoftwareToken = async (user: CognitoUser) =>
  new Promise<string>((resolve, reject) => {
    user.associateSoftwareToken({
      associateSecretCode: (secretCode) => resolve(secretCode),
      onFailure: reject,
    });
  });

/**
 * Verifies the TOTP code during setup
 */
export const verifySoftwareToken = async (
  user: CognitoUser,
  verificationCode: string,
  friendlyDeviceName?: string,
) => new Promise<void>((resolve, reject) => {
  user.verifySoftwareToken(verificationCode, friendlyDeviceName ?? 'Your Device', {
    onSuccess: () => resolve(),
    onFailure: reject,
  });
});
